import React, { lazy, Suspense, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

// Eager load - critical path
import Home from "./pages/Home";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";

// Lazy load - not needed for initial render
// 2026-10-29: /listings is the static gallery (own + off-plan, all filters) served by nginx.
// The old React Listings page only had own listings, so Off-plan was empty — always hard-load.
function StaticListings() {
  useEffect(() => { window.location.replace("/listings" + window.location.search + window.location.hash); }, []);
  return null;
}
const ListingDetail = lazy(() => import("./pages/ListingDetail"));
import FullPageLoad from "./components/FullPageLoad";
const Insights = lazy(() => import("./pages/Insights"));
const InsightDetail = lazy(() => import("./pages/InsightDetail"));
const About = lazy(() => import("./pages/About"));
const CRM = lazy(() => import("./pages/CRM"));
const FAQ = lazy(() => import("./pages/FAQ"));
const MarketIntelligence = lazy(() => import("./pages/MarketIntelligence"));
const BayViews = lazy(() => import("./pages/BayViews"));
const MarinaShores = lazy(() => import("./pages/MarinaShores"));
const BayResidences = lazy(() => import("./pages/BayResidences"));
const SofiaChat = lazy(() => import("./components/SofiaChat"));
const Find = lazy(() => import("./pages/Find"));
const SearchPage = lazy(() => import("./pages/Search"));

// SEO Landing Pages
const DubaiLuxuryRealEstate = lazy(() => import("./pages/seo/DubaiLuxuryRealEstate"));
const RAKPropertyInvestment = lazy(() => import("./pages/seo/RAKPropertyInvestment"));
const HayatIslandApartments = lazy(() => import("./pages/seo/HayatIslandApartments"));
const RitzCarltonResidences = lazy(() => import("./pages/seo/RitzCarltonResidences"));
const RAKvsDubaiInvestment = lazy(() => import("./pages/seo/RAKvsDubaiInvestment"));
const DubaiRentalYields = lazy(() => import("./pages/seo/DubaiRentalYields"));
const RAKHighestYieldAreas = lazy(() => import("./pages/seo/RAKHighestYieldAreas"));
const GoldenVisaInvestment = lazy(() => import("./pages/seo/GoldenVisaInvestment"));

// Market Search — live UAE property search via RapidAPI
const Search = lazy(() => import("./pages/Search"));

// GEO Pages — data-dense, AI-crawler optimised
const BayViewsFacts = lazy(() => import("./pages/BayViewsFacts"));
const BayViewsAr = lazy(() => import("./pages/BayViewsAr"));
const BayViewsRu = lazy(() => import("./pages/BayViewsRu"));

// Off-Plan Intelligence
const OffPlan = lazy(() => import("./pages/OffPlan"));
const OffPlanDetail = lazy(() => import("./pages/OffPlanDetail"));

// Developer Intelligence
const DeveloperDirectory = lazy(() => import("./pages/DeveloperDirectory"));
const DeveloperProfile = lazy(() => import("./pages/DeveloperProfile"));

// Area Intelligence — district/area SEO guides
const AreaDirectory = lazy(() => import("./pages/AreaDirectory"));
const DistrictGuide = lazy(() => import("./pages/DistrictGuide"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

// Lightweight scroll progress - extracted to avoid loading full framer-motion
function ScrollProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docHeight > 0 ? scrollTop / docHeight : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div
      style={{ transform: `scaleX(${progress})` }}
      className="fixed top-0 left-0 right-0 h-[2px] bg-gold z-[100] origin-left"
      aria-hidden="true"
    />
  );
}

// Page loading fallback
const PageFallback = () => (
  <div className="min-h-[50vh] flex items-center justify-center bg-bone">
    <div className="text-ink/50 text-xs uppercase tracking-widest">Loading...</div>
  </div>
);

// These routes are standalone "dispatch" / broker-brief pages that render their
// own masthead. Suppress the global site nav there so it doesn't stack into a
// second top bar (the double-header bug). Match exact path and trailing slash.
const NO_GLOBAL_HEADER = ["/bay-views", "/bay-residences", "/marina-shores"];
function SiteHeader() {
  const { pathname } = useLocation();
  const p = pathname.replace(/\/+$/, "") || "/";
  if (NO_GLOBAL_HEADER.includes(p)) return null;
  return <Header />;
}

export default function App() {
  // Defer chat mount until after first interaction or 2s timeout
  const [chatReady, setChatReady] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setChatReady(true), 2000);
    const onInteract = () => { setChatReady(true); clearTimeout(timer); };
    window.addEventListener("mousemove", onInteract, { once: true, passive: true });
    window.addEventListener("touchstart", onInteract, { once: true, passive: true });
    window.addEventListener("scroll", onInteract, { once: true, passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("mousemove", onInteract);
      window.removeEventListener("touchstart", onInteract);
      window.removeEventListener("scroll", onInteract);
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="min-h-screen bg-bone text-ink flex flex-col relative" data-testid="app-root">
        <div className="grain-overlay" aria-hidden="true" />
        <ScrollProgress />
        <SiteHeader />
        <main className="flex-1">
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/listings" element={<StaticListings />} />
              <Route path="/listings/:slug" element={<FullPageLoad fallback={<ListingDetail />} />} />
              <Route path="/insights" element={<Insights />} />
              <Route path="/insights/:slug" element={<InsightDetail />} />
              <Route path="/about" element={<About />} />
              <Route path="/am/about" element={<About lang="am" />} />
              <Route path="/crm" element={<CRM />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/market-intelligence" element={<MarketIntelligence />} />
              <Route path="/bay-views" element={<BayViews />} />
              <Route path="/marina-shores" element={<MarinaShores />} />
              <Route path="/bay-residences" element={<BayResidences />} />
              <Route path="/bayviews" element={<Navigate to="/bay-views" replace />} />
              <Route path="/find" element={<Find />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/search" element={<Search />} />
              {/* SEO Landing Pages */}
              <Route path="/dubai-luxury-real-estate" element={<DubaiLuxuryRealEstate />} />
              <Route path="/rak-property-investment" element={<RAKPropertyInvestment />} />
              <Route path="/hayat-island-apartments" element={<HayatIslandApartments />} />
              <Route path="/ritz-carlton-residences" element={<RitzCarltonResidences />} />
              <Route path="/rak-vs-dubai-investment" element={<RAKvsDubaiInvestment />} />
              <Route path="/dubai-rental-yields-roi" element={<DubaiRentalYields />} />
              <Route path="/rak-highest-yield-areas" element={<RAKHighestYieldAreas />} />
              <Route path="/uae-golden-visa-property-investment" element={<GoldenVisaInvestment />} />
              {/* GEO Pages — AI-crawler optimised */}
              <Route path="/facts/bay-views" element={<BayViewsFacts />} />
              <Route path="/ar/bay-views" element={<BayViewsAr />} />
              <Route path="/ru/bay-views" element={<BayViewsRu />} />
              {/* Off-Plan Intelligence */}
              <Route path="/offplan" element={<OffPlan />} />
              <Route path="/offplan/:id" element={<OffPlanDetail />} />
              {/* Developer Intelligence */}
              <Route path="/developers" element={<DeveloperDirectory />} />
              <Route path="/developers/:slug" element={<DeveloperProfile />} />
              {/* Area Intelligence — district SEO guides */}
              <Route path="/areas" element={<AreaDirectory />} />
              <Route path="/areas/:district" element={<DistrictGuide />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        {chatReady && (
          <Suspense fallback={null}>
            <SofiaChat />
          </Suspense>
        )}
        <Toaster position="bottom-center" richColors />
      </div>
    </BrowserRouter>
  );
}
